import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, Users, Wifi, Tv, CreditCard, RefreshCw,
  MessageSquare, Server, BarChart2, LogOut, Menu,
  UserCog, Bell, FileUp, ShieldCheck, Router
} from 'lucide-react';

const navItems = [
  { section: 'MAIN' },
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { section: 'CUSTOMERS' },
  { to: '/kyc', icon: ShieldCheck, label: 'KYC & Collection' },
  { to: '/customers', icon: Users, label: 'Customers' },
  { to: '/renewals', icon: RefreshCw, label: 'Renewals' },
  { section: 'BILLING' },
  { to: '/payments', icon: CreditCard, label: 'Payments' },
  { section: 'SUPPORT' },
  { to: '/complaints', icon: MessageSquare, label: 'Complaints' },
  { section: 'NETWORK' },
  { to: '/devices', icon: Server, label: 'Device Inventory' },
  { to: '/acs', icon: Router, label: 'ONT Control (ACS)', adminOnly: true },
  { section: 'ADMIN' },
  { to: '/plans', icon: Wifi, label: 'Plans & Packages', adminOnly: true },
  { to: '/reports', icon: BarChart2, label: 'Reports' },
  { to: '/users', icon: UserCog, label: 'Staff Users', adminOnly: true },
  { to: '/import', icon: FileUp, label: 'Import Data', adminOnly: true },
];

export default function AdminLayout() {
  const { user, logout, isAdmin, isMaster, companyName, showLogo } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getPageTitle = () => {
    const path = window.location.pathname;
    const item = navItems.find(n => n.to === path || (n.to && path.startsWith(n.to + '/')));
    return item?.label || companyName;
  };

  return (
    <div className="admin-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-90 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 90 }}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          {showLogo && <img src="/logo.png" alt={companyName} style={{ height: 52, objectFit: 'contain', marginBottom: 6 }} />}
          <h1>{companyName}</h1>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item, i) => {
            if (item.section) {
              return <div key={i} className="nav-section-label">{item.section}</div>;
            }
            if (item.adminOnly && !isAdmin) return null;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon size={16} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="avatar-circle">{user?.name?.[0]?.toUpperCase()}</div>
            <div className="sidebar-user-info">
              <p>{user?.name}</p>
              <span style={isMaster ? { color: '#f59e0b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' } : {}}>
                {isMaster ? '★ Master' : user?.role}
              </span>
            </div>
          </div>
          <button className="btn-logout" onClick={handleLogout}>
            <LogOut size={13} style={{ display: 'inline', marginRight: 6 }} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="main-content">
        <header className="topbar">
          <div className="flex items-center gap-3" style={{ flexShrink: 0 }}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="mobile-menu-btn"
            >
              <Menu size={20} />
            </button>
            <span className="topbar-title">{getPageTitle()}</span>
          </div>

          <div className="topbar-right">
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 6, borderRadius: 6 }}>
              <Bell size={18} />
            </button>
            <div className="avatar-circle" style={{ width: 30, height: 30, fontSize: '.75rem' }}>
              {user?.name?.[0]?.toUpperCase()}
            </div>
          </div>
        </header>

        <main className="page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
